import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import box_app_access_level from "./images/box_app_access_level.png";
import box_app_scopes from "./images/box_app_scopes.png";
import box_authorize_app from "./images/box_authorize_app.png";
import box_create_platform_app from "./images/box_create_platform_app.png";
import box_custom_app_authentication_method from "./images/box_custom_app_authentication_method.png";
import box_custom_app_type from "./images/box_custom_app_type.png";
import box_enterprise_id from "./images/box_enterprise_id.png";
import box_fetch_client_secret from "./images/box_fetch_client_secret.png";
import box_fill_custom_app_details from "./images/box_fill_custom_app_details.png";
import box_oauth_credentials from "./images/box_oauth_credentials.png";
import box_pending_authorization from "./images/box_pending_authorization.png";
import box_review_and_submit from "./images/box_review_and_submit.png";
import box_submit_app from "./images/box_submit_app.png";

const BoxIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("box");
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const [loading, setLoading] = useState(false);
  const [enterpriseId, setEnterpriseId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showClientSecret, setShowClientSecret] = useState(false);
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerBoxIntegration } = useAPI();

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!enterpriseId) {
      Toast.error("Enterprise ID is required");
      return;
    }
    if (!clientId) {
      Toast.error("Client ID is required");
      return;
    }
    if (!clientSecret) {
      Toast.error("Client Secret is required");
      return;
    }

    setLoading(true);
    try {
      await registerBoxIntegration(enterpriseId, clientId, clientSecret);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Box integration successful");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowClientSecret = () => {
    setShowClientSecret(!showClientSecret);
  };

  const handleMouseDownShowClientSecret = (event) => {
    event.preventDefault();
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will automatically read the configurations from Box and prioritize the suggested remediations."
            : ""
        }
        image={logos.BoxLogo}
        name={"Box"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Box is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <form onSubmit={onSubmit}>
                <GuidelineStepByStep>
                  <GuidelineStep title="Login to the Developer Console">
                    <TextWithExternalLink
                      text="Navigate to the "
                      linkText="Box Developer Console"
                      href="https://cloud.app.box.com/developers/console/"
                    />
                    <Typography variant="body2">
                      You must be logged in as a <b>Co-admin</b> or <b>Admin</b>{" "}
                      user to complete this integration setup.
                    </Typography>
                  </GuidelineStep>
                  <GuidelineStep title="Create a new Custom App">
                    <Typography variant="body2">
                      Click on <b>'Create Platform App'</b>
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_create_platform_app,
                          altext: "Box Create Platform App",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Select <b>'Custom App'</b> as the app type.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_custom_app_type,
                          altext: "Box Custom App Type",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Fill the following details (Optional fields can be left
                      with the default values):
                      <ul>
                        <li
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <span>
                            <b>App Name:</b> Breeze
                          </span>
                          <CustomCopyButton
                            copyTitle="App Name"
                            copyText="Breeze"
                            width="14px"
                          />
                        </li>
                        <li
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <span>
                            <b>Purpose:</b> Integration
                          </span>
                        </li>
                        <li
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <span>
                            <b>Categories:</b> Security & Compliance
                          </span>
                        </li>
                        <li
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <span>
                            <b>
                              Which external system are you integrating with?{" "}
                            </b>
                            Breeze Platform
                          </span>
                          <CustomCopyButton
                            copyTitle="External system"
                            copyText="Breeze Platform"
                            width="14px"
                          />
                        </li>
                      </ul>
                    </Typography>
                    <Typography variant="body2">
                      Press <b>'Next'</b> to continue.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_fill_custom_app_details,
                          altext: "Box Fill Custom App Details",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      For <b>'Authentication Method'</b>, select{" "}
                      <b>'Server Authentication (Client Credentials Grant)'</b>
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_custom_app_authentication_method,
                          altext: "Box Custom App Authentication Method",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Press <b>'Create App'</b> to continue.
                    </Typography>
                  </GuidelineStep>
                  <GuidelineStep title="Configure the Custom App Scopes">
                    <Typography variant="body2">
                      In the App settings, Navigate to <b>'Configuration'</b>{" "}
                      and scroll down to <b>'App Access Level'</b>.
                    </Typography>
                    <Typography variant="body2">
                      Change the <b>'App Access Level'</b> to{" "}
                      <b>'App + Enterprise'</b>.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_app_access_level,
                          altext: "Box App Access Level",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Scroll down to <b>'Application Scopes'</b> and make sure{" "}
                      <b>only</b> the following scopes are checked:
                    </Typography>
                    <Typography variant="body2">
                      <ul>
                        <li>
                          <b>Manage Users</b>
                        </li>
                        <li>
                          <b>Manage Groups</b>
                        </li>
                        <li>
                          <b>Manage enterprise properties</b>
                        </li>
                      </ul>
                    </Typography>
                    <Typography variant="body2">
                      <b>Uncheck</b> all other scopes. Press{" "}
                      <b>'Save Changes'</b> to save the changes.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_app_scopes,
                          altext: "Box App Scopes",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Copy App Credentials">
                    <TextWithExternalLink
                      text="You can find all the credentials on the "
                      linkText="Developer Console"
                      href="https://cloud.app.box.com/developers/console/"
                    />
                    <GuidelineInstruction text="Click on your app, and navigate to 'General Settings' tab. Copy your Enterprise ID." />
                    <TextField
                      required
                      fullWidth
                      variant="filled"
                      name="enterpriseId"
                      label="Enterprise ID"
                      value={enterpriseId}
                      onChange={(e) => setEnterpriseId(e.target.value)}
                      autoComplete="off"
                      disabled={guideMode}
                      sx={{ mb: 2 }}
                    />
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_enterprise_id,
                          altext: "Box Enterprise ID",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <GuidelineInstruction text="Navigate to 'Configuration' tab and scroll down to 'OAuth 2.0 Credentials'. Copy the Client ID." />
                    <TextField
                      required
                      fullWidth
                      variant="filled"
                      name="clientId"
                      label="Client ID"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      autoComplete="off"
                      disabled={guideMode}
                      sx={{ mb: 2 }}
                    />
                    <GuidelineInstruction text="Click 'Fetch Client Secret' and use MFA to complete the process. Copy the Client Secret." />
                    <TextField
                      required
                      fullWidth
                      variant="filled"
                      name="clientSecret"
                      label="Client Secret"
                      type={showClientSecret ? "text" : "password"}
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      autoComplete="off"
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle client secret visibility"
                              onClick={handleClickShowClientSecret}
                              onMouseDown={handleMouseDownShowClientSecret}
                            >
                              {showClientSecret ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_fetch_client_secret,
                          altext: "Box Fetch Client Secret",
                        },
                        {
                          file: box_oauth_credentials,
                          altext: "Box OAuth 2.0 Credentials",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Submit the Custom App for Approval">
                    <Typography variant="body2">
                      Navigate to <b>'Authorization'</b> tab.
                    </Typography>
                    <Typography variant="body2">
                      Click on <b>'Review and Submit'</b> to submit the app for
                      approval by an Admin.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_review_and_submit,
                          altext: "Box Review and Submit",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Fill the app description. For example, "Integration
                      Application for Breeze Security".
                    </Typography>
                    <Typography variant="body2">
                      Click <b>'Submit'</b>.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_submit_app,
                          altext: "Box Review and Submit App",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Authorize the Custom App">
                    <Typography variant="body2">
                      Once the app is submitted, an Admin will have to approve
                      it.
                    </Typography>
                    <TextWithExternalLink
                      text="Navigate to the "
                      linkText="Admin Console Integrations Page"
                      href="https://cloud.app.box.com/master/settings/integrations"
                    />
                    <Typography variant="body2">
                      Navigate to the <b>'Platform Apps Manager'</b> tab.
                    </Typography>
                    <Typography variant="body2">
                      You will see your app as <b>'Pending Authorization'</b>.
                      Click <b>'View'</b> to see the app details.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_pending_authorization,
                          altext: "Box Pending Authorization",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Click <b>'Authorize'</b> to authorize the app.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: box_authorize_app,
                          altext: "Box Authorize App",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="All done, now hit 'Connect' to integrate!">
                    <Box display="flex" justifyContent="center">
                      <PrimaryButton type="submit" disabled={guideMode}>
                        Connect
                      </PrimaryButton>
                    </Box>
                  </GuidelineStep>
                </GuidelineStepByStep>
              </form>
            </div>
          )
        }
      />
    </Box>
  );
};

export default BoxIntegration;
